import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "app/store";
import { IHttpError } from "types/http";
import {
  IZnsCampaign,
  IZnsCampaignListParams,
  IZnsCampaignListResponse,
  IZnsCampaignConfigurationListParams,
  IZnsCampaignConfigurationListResponse,
  IZnsCampaignConfigurationListItem,
  SubmitAutoZnsCampaignPayload,
  DeactivateZnsCampaignPayload,
} from "../types/znsCampaign";

export interface IZNSCampaignCondition {
  category: string;
  displayName: string;
  enumValues?: {
    value: string;
    displayName: string;
  }[];
  fieldName: string;
  operators: string[];
  valueType: 'enum' | 'string' | 'number' | 'boolean' | 'date' | 'campaignData';
}

export interface IZnsCampaignState {
  loading: boolean;
  results: IZnsCampaign[];
  totalCount: number;
  error?: IHttpError;
  createLoading: boolean;
  createSuccess: boolean;
  createError?: IHttpError;
  olResults: any[];
  olTotalCount: number;
  olLoading: boolean;
  conditions: any[];
  /** GET /all/configurations — separate from main campaign list */
  configurationListLoading: boolean;
  configurationListResults: IZnsCampaignConfigurationListItem[];
  configurationListTotalCount: number;
  configurationListError?: IHttpError;
  znsCampaignDeactivateLoading: boolean;
  znsCampaignDeactivateError?: IHttpError;
  /** Incremented after successful deactivate (config + status) so UI can close dialogs */
  znsCampaignDeactivateSuccessNonce: number;
}

const initialState: IZnsCampaignState = {
  loading: false,
  results: [],
  totalCount: 0,
  createLoading: false,
  createSuccess: false,
  olResults: [],
  olTotalCount: 0,
  olLoading: false,
  conditions: [],
  configurationListLoading: false,
  configurationListResults: [],
  configurationListTotalCount: 0,
  znsCampaignDeactivateLoading: false,
  znsCampaignDeactivateSuccessNonce: 0,
};

export const znsCampaignSlice = createSlice({
  name: "znsCampaign",
  initialState,
  reducers: {
    loadZnsCampaigns: (state, _action: PayloadAction<IZnsCampaignListParams>) => {
      state.loading = true;
      state.error = undefined;
    },
    loadZnsCampaignsSuccess: (
      state,
      action: PayloadAction<IZnsCampaignListResponse>
    ) => {
      state.loading = false;
      const payload = action.payload as any;
      const data = payload.results || payload.data;
      state.results = Array.isArray(data) ? data : [];
      state.totalCount =
        typeof payload.totalCount === "number"
          ? payload.totalCount
          : typeof payload.total === "number"
            ? payload.total
            : 0;
    },
    loadZnsCampaignsFailed: (state, action: PayloadAction<IHttpError>) => {
      state.loading = false;
      state.error = action.payload;
    },
    loadZnsCampaignConfigurations: (
      state,
      _action: PayloadAction<IZnsCampaignConfigurationListParams>,
    ) => {
      state.configurationListLoading = true;
      state.configurationListError = undefined;
    },
    loadZnsCampaignConfigurationsSuccess: (
      state,
      action: PayloadAction<IZnsCampaignConfigurationListResponse>,
    ) => {
      state.configurationListLoading = false;
      const payload = action.payload as any;
      state.configurationListResults = Array.isArray(payload.results)
        ? payload.results
        : [];
      state.configurationListTotalCount =
        typeof payload.totalCount === "number" ? payload.totalCount : 0;
    },
    loadZnsCampaignConfigurationsFailed: (
      state,
      action: PayloadAction<IHttpError>,
    ) => {
      state.configurationListLoading = false;
      state.configurationListError = action.payload;
    },
    deactivateZnsCampaign: (
      state,
      _action: PayloadAction<DeactivateZnsCampaignPayload>,
    ) => {
      state.znsCampaignDeactivateLoading = true;
      state.znsCampaignDeactivateError = undefined;
    },
    deactivateZnsCampaignSuccess: (state) => {
      state.znsCampaignDeactivateLoading = false;
      state.znsCampaignDeactivateSuccessNonce += 1;
    },
    deactivateZnsCampaignFailed: (state, action: PayloadAction<IHttpError>) => {
      state.znsCampaignDeactivateLoading = false;
      state.znsCampaignDeactivateError = action.payload;
    },
    createZnsCampaign: (state, _action: PayloadAction<IZnsCampaign>) => {
      state.createLoading = true;
      state.createSuccess = false;
      state.createError = undefined;
    },
    submitAutoZnsCampaign: (
      state,
      _action: PayloadAction<SubmitAutoZnsCampaignPayload>,
    ) => {
      state.createLoading = true;
      state.createSuccess = false;
      state.createError = undefined;
    },
    createZnsCampaignSuccess: (state) => {
      state.createLoading = false;
      state.createSuccess = true;
    },
    createZnsCampaignFailed: (state, action: PayloadAction<IHttpError>) => {
      state.createLoading = false;
      state.createError = action.payload;
    },
    resetCreateStatus: (state) => {
      state.createSuccess = false;
      state.createError = undefined;
    },
    downloadErrorFile: (state, _action: PayloadAction<string>) => {
      state.loading = true;
    },
    downloadErrorFileSuccess: (state) => {
      state.loading = false;
    },
    downloadErrorFileFailed: (state, action: PayloadAction<IHttpError>) => {
      state.loading = false;
      state.error = action.payload;
    },
    loadOlCampaigns: (state, _action: PayloadAction<any>) => {
      state.olLoading = true;
      state.error = undefined;
    },
    loadOlCampaignsSuccess: (state, action: PayloadAction<any>) => {
      state.olLoading = false;
      state.olResults = action.payload?.results || [];
      state.olTotalCount = action.payload?.totalCount || 0;
    },
    loadOlCampaignsFailed: (state, action: PayloadAction<IHttpError>) => {
      state.olLoading = false;
      state.error = action.payload;
    },
    loadZnsCampaignConditions: (state) => {
      state.loading = true;
      state.error = undefined;
    },
    loadZnsCampaignConditionsSuccess: (state, action: PayloadAction<any[]>) => {
      state.loading = false;
      state.conditions = action.payload;
    },
    loadZnsCampaignConditionsFailed: (state, action: PayloadAction<IHttpError>) => {
      state.loading = false;
      state.error = action.payload;
    },
  },
});

export const {
  loadZnsCampaigns,
  loadZnsCampaignsSuccess,
  loadZnsCampaignsFailed,
  loadZnsCampaignConfigurations,
  loadZnsCampaignConfigurationsSuccess,
  loadZnsCampaignConfigurationsFailed,
  deactivateZnsCampaign,
  deactivateZnsCampaignSuccess,
  deactivateZnsCampaignFailed,
  createZnsCampaign,
  submitAutoZnsCampaign,
  createZnsCampaignSuccess,
  createZnsCampaignFailed,
  resetCreateStatus,
  downloadErrorFile,
  downloadErrorFileSuccess,
  downloadErrorFileFailed,
  loadOlCampaigns,
  loadOlCampaignsSuccess,
  loadOlCampaignsFailed,
  loadZnsCampaignConditions,
  loadZnsCampaignConditionsSuccess,
  loadZnsCampaignConditionsFailed,
} = znsCampaignSlice.actions;

export const selectZnsCampaignResults = (state: RootState) =>
  state.znsCampaign.results;
export const selectZnsCampaignTotalCount = (state: RootState) =>
  state.znsCampaign.totalCount;
export const selectZnsCampaignLoading = (state: RootState) =>
  state.znsCampaign.loading;
export const selectZnsCampaignError = (state: RootState) =>
  state.znsCampaign.error;
export const selectZnsCampaignCreateLoading = (state: RootState) =>
  state.znsCampaign.createLoading;
export const selectZnsCampaignCreateSuccess = (state: RootState) =>
  state.znsCampaign.createSuccess;
export const selectZnsCampaignCreateError = (state: RootState) =>
  state.znsCampaign.createError;
export const selectOlCampaignResults = (state: RootState) =>
  state.znsCampaign.olResults;
export const selectOlCampaignTotalCount = (state: RootState) =>
  state.znsCampaign.olTotalCount;
export const selectOlCampaignLoading = (state: RootState) =>
  state.znsCampaign.olLoading;
export const selectZnsCampaignConditions = (state: RootState) =>
  state.znsCampaign.conditions;

export const selectZnsCampaignConfigurationListResults = (state: RootState) =>
  state.znsCampaign.configurationListResults;
export const selectZnsCampaignConfigurationListTotalCount = (state: RootState) =>
  state.znsCampaign.configurationListTotalCount;
export const selectZnsCampaignConfigurationListLoading = (state: RootState) =>
  state.znsCampaign.configurationListLoading;
export const selectZnsCampaignConfigurationListError = (state: RootState) =>
  state.znsCampaign.configurationListError;
export const selectZnsCampaignDeactivateLoading = (state: RootState) =>
  state.znsCampaign.znsCampaignDeactivateLoading;
export const selectZnsCampaignDeactivateSuccessNonce = (state: RootState) =>
  state.znsCampaign.znsCampaignDeactivateSuccessNonce;

export default znsCampaignSlice.reducer;
